import { useEffect, useRef } from "react";
import { tv } from "tailwind-variants";

import type { CareplanOrderLine } from "@/domains/careplan/factories/create-careplan-order-model";
import { Button, Counter } from "@/ui";

const MAX_QUANTITY = 5;

const careplanCartOrderLineVariants = tv({
  slots: {
    root: "grid grid-cols-[56px_minmax(0,1fr)_auto] gap-3.5 border-b border-line py-3",
    image: "size-14 rounded-xl bg-product-media object-contain p-1",
    name: "text-lg font-semibold text-content",
    reason: "text-base text-content-secondary",
    note: "text-base text-content-subtle",
    pricing: "flex flex-col items-end gap-1.5",
    price: "text-lg font-semibold text-content",
    priceSuffix: "text-base font-normal text-content-secondary",
    listPrice: "text-base text-content-subtle line-through",
  },
  variants: {
    removed: {
      true: {
        image: "opacity-40",
        name: "text-content-secondary",
        reason: "text-content-subtle",
        pricing: "justify-center",
      },
    },
  },
});

const { image, listPrice, name, note, price, priceSuffix, pricing, reason, root } =
  careplanCartOrderLineVariants();

type CareplanCartOrderLineProps = {
  line: CareplanOrderLine;
  disabled: boolean;
  onQuantityChange: (quantity: number) => void;
  onRestore: () => void;
};

export const CareplanCartOrderLine = ({
  disabled,
  line,
  onQuantityChange,
  onRestore,
}: CareplanCartOrderLineProps) => {
  const removed = !line.isActive;
  const reasonText = line.product?.whyRecommended?.title ?? null;

  const decreaseRef = useRef<HTMLButtonElement>(null);
  const increaseRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<"decrease" | "increase" | null>(null);

  useEffect(() => {
    const pending = pendingFocusRef.current;

    if (!pending || disabled) {
      return;
    }

    pendingFocusRef.current = null;

    if (!line.isActive) {
      addRef.current?.focus();

      return;
    }

    const preferred = pending === "increase" ? increaseRef.current : decreaseRef.current;
    const fallback = pending === "increase" ? decreaseRef.current : increaseRef.current;

    (preferred && !preferred.disabled ? preferred : fallback)?.focus();
  }, [disabled, line.isActive]);

  const handleQuantityChange = (quantity: number) => {
    pendingFocusRef.current = quantity > line.quantity ? "increase" : "decrease";
    onQuantityChange(quantity);
  };

  const handleRestore = () => {
    pendingFocusRef.current = "increase";
    onRestore();
  };

  return (
    <li className={root()}>
      <img alt="" className={image({ removed })} src={line.image} />

      <div className="flex min-w-0 flex-col">
        <p className={name({ removed })}>{line.shortName}</p>
        {reasonText ? <p className={reason({ removed })}>{reasonText}</p> : null}
        {removed ? (
          <p className={note()}>Not in your order. Your plan still recommends it.</p>
        ) : null}
        {line.quantity > 1 ? <p className={note()}>Your plan recommends 1 per month</p> : null}
      </div>

      <div className={pricing({ removed })}>
        {removed ? (
          <Button
            disabled={disabled}
            onClick={handleRestore}
            ref={addRef}
            size="md"
            variant="secondary-dark"
          >
            Add to Cart
          </Button>
        ) : (
          <>
            <p className={price()}>
              ${line.unitPriceAsString}
              <span className={priceSuffix()}>/mo</span>
            </p>
            <p className={listPrice()}>
              <span className="sr-only">Retail price </span>${line.unitListPriceAsString}/mo
            </p>
            <Counter
              decreaseRef={decreaseRef}
              disabled={disabled}
              increaseRef={increaseRef}
              label={line.shortName}
              max={MAX_QUANTITY}
              min={0}
              onChange={handleQuantityChange}
              value={line.quantity}
            />
          </>
        )}
      </div>
    </li>
  );
};
